import {
  DropdownDivider,
  DropdownGroup,
  DropdownItem,
  useTranslationContext,
} from 'platform/components';
import {Text, Show} from 'platform/foundation';

import {Fragment} from 'react';

import type {ClipboardCopyMode} from '../../utils/buildClipboardTsv';
import type {ContextMenuSection} from '../../utils/getRowContextMenuSections';

export type RowContextMenuContentProps = {
  sections: ContextMenuSection[];
  clipboardEnabled: boolean;
  onActionClick: (actionKey: string, event: React.MouseEvent) => void;
  onCopy: (mode: ClipboardCopyMode) => void;
  /** Toggles the clicked cell's value on the column filter; null hides the item (T20-135948). */
  onFilterByValue?: (() => void) | null;
  /** Truncated display text of the clicked cell, shown as "Filter by: {value}"; null falls back to the plain label. */
  filterByValueLabel?: string | null;
  /** 'remove' switches the wording to "Remove from filter" when the value is already selected. */
  filterByValueMode?: 'add' | 'remove';
};

export function RowContextMenuContent(props: RowContextMenuContentProps) {
  const t = useTranslationContext();
  const hasSections = props.sections.length > 0;
  const hasFilterByValue = props.onFilterByValue != null;

  return (
    <>
      {props.sections.map((section, index) => (
        <Fragment key={section.group ?? '__ungrouped__'}>
          <Show when={index > 0}>
            <DropdownDivider />
          </Show>
          <DropdownGroup label={section.group ?? undefined}>
            {section.items.map((item) => (
              <DropdownItem
                key={item.actionKey}
                label={
                  item.isHighlighted ? (
                    <Text color="link" size="small">
                      {item.title}
                    </Text>
                  ) : (
                    `${item.title}${
                      item.isNewTab
                        ? ` (${t('page.datagrid.labels.newTab', {defaultValue: 'New tab'})})`
                        : ''
                    }`
                  )
                }
                leftIcon={item.icon}
                severity={item.isHighlighted ? 'informational' : undefined}
                onClick={(event) => props.onActionClick(item.actionKey, event)}
                data-testid={`contextMenuItem-${item.actionKey}`}
              />
            ))}
          </DropdownGroup>
        </Fragment>
      ))}

      <Show when={hasFilterByValue}>
        <>
          <Show when={hasSections}>
            <DropdownDivider />
          </Show>
          <DropdownItem
            label={
              props.filterByValueMode === 'remove' // eslint-disable-line no-nested-ternary -- Pre-existing violation
                ? props.filterByValueLabel != null
                  ? t('page.datagrid.contextMenu.removeFromFilterWithValue', {
                      value: props.filterByValueLabel,
                      defaultValue: 'Remove from filter: {value}',
                    })
                  : t('page.datagrid.contextMenu.removeFromFilter', {
                      defaultValue: 'Remove from filter',
                    })
                : props.filterByValueLabel != null
                  ? t('page.datagrid.contextMenu.filterByValueWithValue', {
                      value: props.filterByValueLabel,
                      defaultValue: 'Filter by: {value}',
                    })
                  : t('page.datagrid.contextMenu.filterByValue', {defaultValue: 'Filter by value'})
            }
            leftIcon="content/filter_list"
            onClick={() => props.onFilterByValue?.()}
            data-testid="contextMenuItem-filterByValue"
          />
        </>
      </Show>

      <Show when={props.clipboardEnabled}>
        <>
          <Show when={hasSections || hasFilterByValue}>
            <DropdownDivider />
          </Show>
          <DropdownGroup
            label={t('page.datagrid.contextMenu.clipboard', {defaultValue: 'Clipboard'})}
          >
            <DropdownItem
              label={t('page.datagrid.labels.copy', {defaultValue: 'Copy'})}
              leftIcon="content/copy"
              onClick={() => props.onCopy('DATA')}
              data-testid="contextMenuItem-copy"
            />
            <DropdownItem
              label={t('page.datagrid.labels.copyWithHeaders', {
                defaultValue: 'Copy with headers',
              })}
              leftIcon="content/copy"
              onClick={() => props.onCopy('WITH_HEADERS')}
              data-testid="contextMenuItem-copyWithHeaders"
            />
            <DropdownItem
              label={t('page.datagrid.labels.copyWithHeaderGroups', {
                defaultValue: 'Copy with header groups',
              })}
              leftIcon="content/copy"
              onClick={() => props.onCopy('WITH_HEADER_GROUPS')}
              data-testid="contextMenuItem-copyWithHeaderGroups"
            />
          </DropdownGroup>
        </>
      </Show>
    </>
  );
}
